import { useCallback, useState } from 'react';
import MatchmakingScreen from './screens/MatchmakingScreen';
import GameScreen, { type GameSummary } from './screens/GameScreen';
import GameOverScreen from './screens/GameOverScreen';
import { getOrCreatePlayerId } from './lib/playerId';
import type { MatchDetails } from './protocol';

type Screen =
  | { name: 'matchmaking' }
  | { name: 'game'; match: MatchDetails }
  | { name: 'gameOver'; summary: GameSummary };

/**
 * Top-level screen machine. Mirrors the three Phaser scenes of client-phaser:
 * Matchmaking → Game → GameOver → Matchmaking.
 *
 * Each screen owns its own WebSocket and unmounts when it hands off, so a
 * socket can never outlive the screen that opened it.
 */
export default function App() {
  const [playerId, setPlayerId] = useState(getOrCreatePlayerId);
  const [screen, setScreen] = useState<Screen>({ name: 'matchmaking' });

  const handleMatchReady = useCallback((match: MatchDetails) => {
    setScreen({ name: 'game', match });
  }, []);

  const handleGameOver = useCallback((summary: GameSummary) => {
    setScreen({ name: 'gameOver', summary });
  }, []);

  const handlePlayAgain = useCallback(() => {
    setScreen({ name: 'matchmaking' });
  }, []);

  switch (screen.name) {
    case 'matchmaking':
      return (
        <MatchmakingScreen
          playerId={playerId}
          onPlayerIdChange={setPlayerId}
          onMatchReady={handleMatchReady}
        />
      );
    case 'game':
      return (
        <GameScreen
          key={screen.match.psess}
          playerId={playerId}
          match={screen.match}
          onGameOver={handleGameOver}
        />
      );
    case 'gameOver':
      return (
        <GameOverScreen playerId={playerId} summary={screen.summary} onPlayAgain={handlePlayAgain} />
      );
  }
}
